import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CreditCardIcon, TruckIcon, CheckCircleIcon, ShieldCheckIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CheckoutModal({
  isOpen,
  onClose
}: CheckoutModalProps) {
  const {
    cartTotal,
    clearCart
  } = useCart();
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 2) {
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        setStep(3);
      }, 2000);
    } else {
      setStep(step + 1);
    }
  };
  const handleFinish = () => {
    clearCart();
    onClose();
    setStep(1);
  };
  return <AnimatePresence data-id="element-50">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]" data-id="element-51" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 m-auto w-full max-w-2xl h-fit max-h-[90vh] bg-white rounded-3xl shadow-2xl z-[101] overflow-hidden flex flex-col" data-id="element-52">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-53">
              <div className="flex items-center gap-3" data-id="element-54">
                <div className="w-10 h-10 bg-clinical-blue/10 rounded-xl flex items-center justify-center text-clinical-blue" data-id="element-55">
                  <CreditCardIcon className="w-5 h-5" data-id="element-56" />
                </div>
                <div data-id="element-57">
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight" data-id="element-58">
                    Secure Checkout
                  </h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-59">
                    Step {step} of 3
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors" data-id="element-60">
                <XIcon className="w-5 h-5 text-slate-400" data-id="element-61" />
              </button>
            </div>

            <div className="p-8 overflow-y-auto" data-id="element-62">
              {step === 1 && <form onSubmit={handleNext} className="space-y-6" data-id="element-63">
                  <div className="flex items-center gap-3 mb-4" data-id="element-64">
                    <TruckIcon className="w-6 h-6 text-clinical-blue" data-id="element-65" />
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight" data-id="element-66">
                      Shipping Address
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-67">
                    <input required placeholder="First Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-68" />
                    <input required placeholder="Last Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-69" />
                  </div>
                  <input required placeholder="Street Address" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-70" />
                  <div className="grid grid-cols-3 gap-4" data-id="element-71">
                    <input required placeholder="City" className="col-span-1 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-72" />
                    <input required placeholder="State" className="col-span-1 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-73" />
                    <input required placeholder="ZIP" className="col-span-1 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-74" />
                  </div>
                  <button type="submit" className="w-full bg-clinical-blue text-white font-black py-4 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs" data-id="element-75">
                    Continue to Payment
                  </button>
                </form>}

              {step === 2 && <form onSubmit={handleNext} className="space-y-6" data-id="element-76">
                  <div className="flex items-center gap-3 mb-4" data-id="element-77">
                    <CreditCardIcon className="w-6 h-6 text-clinical-blue" data-id="element-78" />
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight" data-id="element-79">
                      Payment Method
                    </h3>
                  </div>
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4" data-id="element-80">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-4" data-id="element-81">
                      <span className="text-sm font-bold text-slate-900 uppercase tracking-widest" data-id="element-82">
                        Total to Pay
                      </span>
                      <span className="text-xl font-black text-clinical-blue" data-id="element-83">
                        ${cartTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-84">
                      <input required placeholder="Card Number" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-85" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-86">
                        <input required placeholder="MM/YY" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-87" />
                        <input required placeholder="CVC" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 outline-none" data-id="element-88" />
                      </div>
                    </div>
                  </div>
                  <button disabled={isProcessing} type="submit" className="w-full bg-clinical-blue text-white font-black py-4 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs flex items-center justify-center gap-3" data-id="element-89">
                    {isProcessing ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-90"></span> : <>Pay ${cartTotal.toFixed(2)}</>}
                  </button>
                  <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-widest" data-id="element-91">
                    <ShieldCheckIcon className="w-3 h-3" data-id="element-92" />
                    Secure SSL Encrypted Payment
                  </div>
                </form>}

              {step === 3 && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center py-8" data-id="element-93">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-94">
                    <CheckCircleIcon className="w-10 h-10" data-id="element-95" />
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 mb-4 uppercase tracking-tight" data-id="element-96">
                    Order Confirmed!
                  </h3>
                  <p className="text-slate-500 mb-8 leading-relaxed" data-id="element-97">
                    Thank you for your purchase. Your wellness essentials are
                    being prepared for shipment and will arrive in 2-3 business
                    days.
                  </p>
                  <button onClick={handleFinish} className="bg-clinical-blue text-white font-black py-4 px-12 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs shadow-lg shadow-clinical-blue/20" data-id="element-98">
                    Close & Clear Cart
                  </button>
                </motion.div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}